import { Component, Inject, OnInit, Optional } from '@angular/core';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { ModalService } from '@app/core/services/modal.service';
import { TranslateService } from '@app/core/services/translate.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { FactsGet, FactsService, FactsValueType } from '@app/services/facts.service';

@Component({
    selector: 'tt-factsedit',
    templateUrl: './factsedit.component.html',
    styleUrls: ['./factsedit.component.css'],
    standalone: false
})
export class FactseditComponent implements OnInit {
    public page?: FactsGet;

    public valueTypes: FactsValueType[] = [];

    public saving = false;

    public deleting = false;

    constructor(
        private readonly facts: FactsService,
        private readonly modal: ModalService,
        private readonly translate: TranslateService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator
    ) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public async ngOnInit(): Promise<void> {
        await Promise.all([this.loadFacts(), this.loadValueTypes()]);
    }

    private async loadFacts(): Promise<void> {
        const factsId = this.navigator?.params['facts_id'];

        if (factsId === undefined || factsId === null) return;

        this.page = await this.facts.getFacts({ facts_id: String(factsId) });
    }

    private async loadValueTypes(): Promise<void> {
        this.valueTypes = await this.facts.listValueTypes();
    }

    public async saveChanges(): Promise<void> {
        if (!this.page || this.saving || this.deleting) return;

        this.saving = true;

        try {
            await this.handleResponse(await this.facts.saveFacts(this.page));
        } finally { this.saving = false; }
    }

    public async deleteValues(): Promise<void> {
        if (!this.page || this.saving || this.deleting) return;

        this.deleting = true;

        try {
            const confirmed = await this.modal.openConfirmDialog({
                type: 'danger',
                title: 'confirm_title',
                translateTitle: true,
                message: 'confirm_message',
                translateMessage: true,
                ok: 'confirm_ok_label',
                cancel: 'confirm_cancel_label'
            });

            if (confirmed) await this.handleResponse(await this.facts.deleteFacts(this.page));
        } finally { this.deleting = false; }
    }

    private async handleResponse(response: ErrorMessageResponse): Promise<void> {
        if (String(response.errorcode) === '0') {
            await this.navigator?.back();
        } else {
            await this.modal.show({
                type: 'warning',
                title: 'error',
                translateTitle: true,
                message: response.errormessage,
                buttons: [{
                    label: await this.translate.translate('ok'),
                    cssClass: 'btn-warning',
                    close: true,
                    type: 'warning'
                }]
            });
        }
    }
}
